import { ErrorNotice } from "../ui/ErrorNotice.js";
/**
 * QueuePanel (010 T008) — lists render BATCHES (not individual per-template jobs) with
 * status/progress/elapsed and exposes pause/resume/cancel + concurrency control. One
 * "Create Mock-up" run enqueues one job per template but they share a `batchId`, so the queue
 * groups them into a single batch row (templates × designs × views). Clicking a batch row opens
 * a centered details popup listing each template, its views, and per design×view results.
 * Reads the live snapshot from the queue store; actions call the QueueManager via app services.
 */
import { useMemo, useState } from "react";
import { EmptyState } from "../ui/EmptyState.js";
import { useServices } from "../app/context.js";
import { useQueueStore, type LiveJobProgress } from "../stores/queue.js";
import { useBatchStore } from "../stores/batch.js";
import { Gallery } from "../batch/Gallery.js";
import type { JobRecord, JobStatus } from "@genz/platform";
import { Modal } from "../ui/Modal.js";
import { WorkingTimer } from "../ui/WorkingTimer.js";
import "./queue.css";
import { RowAction } from "../ui/RowAction.js";

const STATUS_LABEL: Record<JobStatus, string> = {
  queued: "Queued",
  running: "Running",
  paused: "Paused",
  completed: "Done",
  failed: "Failed",
  canceled: "Canceled",
  interrupted: "Interrupted (restored)",
};

/** Map each job status to a shared status-badge variant (cobalt-info running,
 *  success done, warn waiting, danger failed/interrupted). */
const STATUS_BADGE: Record<JobStatus, string> = {
  queued: "badge--warn",
  running: "badge--info",
  paused: "badge--warn",
  completed: "badge--success",
  failed: "badge--danger",
  canceled: "badge--neutral",
  interrupted: "badge--danger",
};

/**
 * WS-N: is this a Transparent folder conversion rather than a template render? A transparent
 * job carries `views: []`, so every "designs × views" arithmetic below would read 0 for it.
 */
function isTransparentJob(job: JobRecord): boolean {
  return job.runKind === "transparent";
}

/**
 * WS-P: a `bake-missing` failure is a recoverable SYNC state — the template declares a baked
 * mock-up this device has not downloaded — not a crash. It gets its own sentence and WS-B's
 * WARN tier (`statusBadgeClass`'s vocabulary), while the existing error text and the Retry
 * affordance are untouched. Any other failure keeps "Failed" / `--danger`.
 */
const BAKE_MISSING_LABEL = "Mock-up not on this device";

function bakeMissing(jobs: JobRecord[], status: JobStatus): boolean {
  if (status !== "failed") return false;
  // review-1 m-6: `every` FAILED job, not `some` job in the batch. With `some`, a batch where
  // one template lacked its bake and another crashed for an unrelated reason rendered the whole
  // row warn-tier "Mock-up not on this device" — and the `--danger` tier disappeared along with
  // the only signal that something else went wrong. A mixed batch is "Failed", which is the
  // honest label for it; the error text below still carries the first `failureReason`.
  const failed = jobs.filter((j) => j.status === "failed");
  return failed.length > 0 && failed.every((j) => j.failureCode === "bake-missing");
}

/** Total render items per job — designs × views for a mockup render, FILES for a Transparent
 *  conversion (whose `views` is empty by design, so the product would be 0). */
function frameTotal(job: JobRecord): number {
  return isTransparentJob(job) ? job.designCount : job.designCount * job.views.length;
}
function framesDone(job: JobRecord): number {
  return job.items.filter((i) => i.status === "done" || i.status === "failed").length;
}

/** A run's clock: counting while it renders, then frozen on first start → last end once every
 *  job has ended. A run that has not started, or is paused, shows no time. */
function RunTime({ jobs, status }: { jobs: JobRecord[]; status: JobStatus }) {
  const startedAt = Math.min(...jobs.map((j) => j.startedAt ?? Infinity));
  if (!Number.isFinite(startedAt)) return <>—</>;
  if (status === "running") return <WorkingTimer startedAt={startedAt} />;
  if (!jobs.every((j) => j.completedAt)) return <>—</>;
  const endedAt = Math.max(...jobs.map((j) => j.completedAt ?? 0));
  return (
    <WorkingTimer startedAt={startedAt} endedAt={endedAt}
      state={status === "failed" ? "failed" : "done"} label={status === "completed" ? "Took" : undefined} />
  );
}

/** The first failed item's engine error — surfaced so "Failed" is never reasonless.
 *  Maps the engine's lock guard to an actionable hint; other errors show verbatim. */
function failureReason(job: JobRecord): string | undefined {
  const raw = job.items.find((i) => i.status === "failed")?.error;
  if (!raw) return undefined;
  if (/not locked|not.*Active/i.test(raw)) {
    return "Template isn't Active yet — activate it (or create a draft preview).";
  }
  return raw;
}

/** Finished/total frames for a job — preferring the running job's LIVE per-item progress
 *  (Task 3), which fills mid-run, over the `job.items` list, which only fills at job end. */
function framesOf(job: JobRecord, live?: LiveJobProgress): { done: number; total: number } {
  const total = live?.total ?? frameTotal(job);
  const done = live?.completed ?? framesDone(job);
  return { done, total };
}

/** A batch = every job sharing a `batchId` (legacy/restored jobs fall back to their own jobId,
 *  i.e. a batch of one). */
interface BatchGroup {
  batchId: string;
  jobs: JobRecord[];
}

/** Group the flat job list into batches, preserving queue order by the earliest job position. */
function groupBatches(jobs: JobRecord[]): BatchGroup[] {
  const byBatch = new Map<string, JobRecord[]>();
  for (const job of jobs) {
    const key = job.batchId ?? job.jobId;
    const arr = byBatch.get(key);
    if (arr) arr.push(job);
    else byBatch.set(key, [job]);
  }
  // NEWEST BATCH FIRST. The map preserved arrival order, so the run you just started appeared
  // at the BOTTOM and every new batch pushed the list further down — on a busy day the thing
  // you are actually watching is off-screen. A batch is dated by its earliest `queuedAt`, so
  // the group's position is stable while its own jobs start and finish.
  return [...byBatch.entries()]
    .map(([batchId, groupJobs]) => ({ batchId, jobs: groupJobs }))
    .sort((a, b) => batchQueuedAt(b.jobs) - batchQueuedAt(a.jobs));
}

/** When a batch was enqueued = the earliest `queuedAt` among its jobs. */
function batchQueuedAt(jobs: JobRecord[]): number {
  return jobs.reduce((min, j) => Math.min(min, j.queuedAt ?? 0), Number.POSITIVE_INFINITY);
}

/** Aggregate status for a batch: completed only when ALL are; otherwise the most "active" wins. */
function batchStatus(jobs: JobRecord[]): JobStatus {
  const has = (s: JobStatus) => jobs.some((j) => j.status === s);
  // Still in flight → reflect the active phase.
  if (has("running")) return "running";
  if (has("queued")) return "queued";
  if (has("paused")) return "paused";
  // Every job is terminal now. Any STILL-FAILING item makes the batch a failure; a clean
  // finish (or a fully recovered one) is Done. (A partial-failure job is "completed" at the
  // job level, but its failed items still count as an error for the batch summary.)
  // Review-2 L-8: the verdict comes from the RECONCILED item set, not from `job.status` — a
  // job that lost every item is stuck at "failed" forever, so after a successful retry the row
  // kept a red "Failed" badge with no error under it (`failureReason` was already gone). A
  // whole-job failure that produced no `done` row at all is of course still a failure.
  // Review-3 M-3: "has a `done` row" is NOT the same as "recovered" — a job that checkpointed
  // on pause (its `done` rows merged in) and then threw wholesale on resume also has `done`
  // rows, with `status: "failed"` and no `failed` row at all (`manager.ts:567-579` merges the
  // pre-pause segment on pause; the `catch` at `:630-641` sets `status: "failed"` and never
  // touches `items`). Compare recovered COVERAGE against what the job planned instead of just
  // checking "any done row exists": fewer done rows than `designCount * views.length` means
  // real output is still missing, so it stays a failure.
  const anyFailure =
    failingItemKeys(jobs, retryRoots(jobs)).size > 0 ||
    jobs.some(
      (j) =>
        j.status === "failed" &&
        j.items.filter((i) => i.status === "done").length < frameTotal(j),
    );
  if (anyFailure) return "failed";
  if (has("interrupted")) return "interrupted";
  if (has("canceled")) return "canceled";
  return "completed"; // finished with no errors → Done
}

/**
 * The identity of one render item inside a batch.
 *
 * `designSlug:view` alone is NOT unique in a batch: a batch is one job PER TEMPLATE sharing a
 * `batchId`, and every template job renders the SAME designs (`BatchPanel`), so two templates
 * carry the same pair for two DIFFERENT outputs (review-2 M-2). The retry chain's ROOT job id
 * separates them — while a source job and its retries share one root, which is what lets a
 * recovered item stop counting as failed (review-1 M-1).
 */
const itemKey = (root: string, i: { designSlug: string; view: string }) =>
  `${root}:${i.designSlug}:${i.view}`;

/** jobId → the id of the job that starts its retry chain (itself, when it is not a retry).
 *  Cycle-guarded; an ancestor missing from the batch is still a stable root id. */
function retryRoots(jobs: JobRecord[]): Map<string, string> {
  const byId = new Map(jobs.map((j) => [j.jobId, j]));
  const roots = new Map<string, string>();
  for (const job of jobs) {
    const seen = new Set<string>([job.jobId]);
    let cur = job;
    let root = job.jobId;
    while (cur.retryOf && !seen.has(cur.retryOf)) {
      seen.add(cur.retryOf);
      root = cur.retryOf;
      const parent = byId.get(cur.retryOf);
      if (!parent) break;
      cur = parent;
    }
    roots.set(job.jobId, root);
  }
  return roots;
}

/**
 * The items of a batch that are STILL failing — the "Retry N failed" count.
 *
 * Review-1 M-1: a retry job re-renders the same item, so the source job's row and the retry
 * job's row are the SAME item, not two. Summing every `failed` row across the batch counted a
 * retried item twice and — worse — kept counting it after it had been recovered. A key that
 * has a `done` row anywhere in ITS OWN retry lineage has succeeded and is never offered again;
 * another template's success for the same design × view says nothing about this one.
 */
function failingItemKeys(jobs: JobRecord[], roots: Map<string, string>): Set<string> {
  const failing = new Set<string>();
  const done = new Set<string>();
  for (const job of jobs) {
    const root = roots.get(job.jobId) ?? job.jobId;
    for (const i of job.items) {
      if (i.status === "done") done.add(itemKey(root, i));
      else if (i.status === "failed") failing.add(itemKey(root, i));
    }
  }
  for (const k of done) failing.delete(k);
  return failing;
}

/** Aggregate frame progress across a batch's jobs (live-aware per job). */
function batchFrames(jobs: JobRecord[], live: Record<string, LiveJobProgress>) {
  let done = 0;
  let total = 0;
  for (const job of jobs) {
    const f = framesOf(job, live[job.jobId]);
    done += f.done;
    total += f.total;
  }
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return { done, total, pct };
}

export function QueuePanel() {
  const { queue, io } = useServices();
  const jobs = useQueueStore((s) => s.jobs);
  const live = useQueueStore((s) => s.live);
  // F-8: this device is not writing the queue to disk. The run itself is fine — the engine is
  // rendering and the writer is writing — but a restart loses it, and nothing on this page said
  // so (the job trace did, and nothing renders traces here).
  const persistDegraded = useQueueStore((s) => s.persistDegraded);
  const concurrency = useQueueStore((s) => s.concurrency);
  const setConcurrency = useQueueStore((s) => s.setConcurrency);
  // The Queue page also shows the run's results as they land — the SAME shared Gallery the
  // Batch tab uses, fed by the batch store so finished mockups appear here mid-run (Task 3).
  /**
   * Job id → the folder THAT job's run wrote into, known from its first file.
   *
   * NOT `lastSavedPath`: that is one slot shared by both arms, so a single Transparent
   * conversion re-pointed every row's "Open folder" at the overlay folder (review F-4). A list
   * of runs needs a folder per run, and a row that has written nothing offers no button.
   */
  const savedPathByJob = useBatchStore((s) => s.savedPathByJob);
  const outputs = useBatchStore((s) => s.outputs);
  const batchProgress = useBatchStore((s) => s.progress);

  /**
   * A refused reveal, and the row it belongs to (review F-2).
   *
   * The row's handler used to be a bare `void io.openPath!(…)`: a file manager that will not
   * start produced an unhandled rejection and a button that looked broken. This is the shape
   * `BatchPanel` and the batch-details modal already use — clear, try, and name the failure
   * where the operator pressed — carrying the batch id because ONE panel state serves a list.
   */
  const [openErr, setOpenErr] = useState<{ batchId: string; message: string } | null>(null);
  const openFolder = (batchId: string, path: string): void => {
    setOpenErr(null);
    void io?.openPath?.(path).catch((e: unknown) =>
      setOpenErr({ batchId, message: e instanceof Error ? e.message : String(e) }));
  };

  const batches = useMemo(() => groupBatches(jobs), [jobs]);
  // The batch open in the details popup (by batchId), or null.
  const [openBatchId, setOpenBatchId] = useState<string | null>(null);
  const openBatch = openBatchId ? batches.find((b) => b.batchId === openBatchId) ?? null : null;

  return (
    <section className="queue" data-testid="queue-panel">
      <header className="queue__header">
        {/* R11 (Direction B): this was `<h3 className="queue__title">`, the third of three
            page-title idioms across these four surfaces. `.panel-title` is the survivor, and
            it is an `<h2>` because `ExportWorkspace` already owns the route's one `<h1>`. */}
        <h2 className="panel-title">Mock-up queue</h2>
        <label>
          Concurrency
          <input
            type="number"
            min={1}
            max={8}
            value={concurrency}
            onChange={(e) => {
              const n = Number(e.target.value) || 1;
              setConcurrency(n);
              queue.setConcurrency(n);
            }}
          />
        </label>
      </header>

      {/* WS-ERR F-8 — the queue is not durable on this device. `role="alert"`: the operator
          is about to lose a run they think is safe, and the only recovery (do not close the
          app until it finishes) has to be known BEFORE they close it. */}
      {persistDegraded && (
        <p className="banner banner--danger" role="alert" data-testid="queue-persist-degraded">
          This device is not saving the queue to disk ({persistDegraded}). The run is still
          rendering and files are still written to your save folder, but closing the app will
          lose anything that has not finished.
        </p>
      )}
      {batches.length === 0 ? (
        <EmptyState
          icon="list"
          title="No batches queued"
          hint="Start a batch from the Batch tab and it will appear here with live progress."
        />
      ) : (
        <ul className="queue__list card card--flush">
          {batches.map((batch) => {
            const status = batchStatus(batch.jobs);
            const { done, total, pct } = batchFrames(batch.jobs, live);
            const designCount = Math.max(0, ...batch.jobs.map((j) => j.designCount));
            const templateCount = batch.jobs.length;
            // WS-N: a Transparent conversion is a batch of one with no template and no views.
            // `every` rather than `some`: a mixed batch cannot happen today (a conversion gets
            // its own batchId), and if one ever did, the template wording is the safe default.
            const transparent = batch.jobs.every(isTransparentJob);
            // THIS run's folder. Every job of a multi-template run shares one writer and so one
            // folder, which is why the first job that answers is the right answer for the row.
            const rowFolder = batch.jobs.map((j) => savedPathByJob[j.jobId]).find(Boolean);
            // "Preparing assets…" — before the first render tick of the batch's active job.
            const preparing =
              done === 0 && batch.jobs.some((j) => live[j.jobId]?.phase === "preparing");
            const canPause = batch.jobs.some((j) => j.status === "queued" || j.status === "running");
            const canResume = batch.jobs.some((j) => j.status === "paused");
            const canCancel = batch.jobs.some((j) => j.status !== "completed" && j.status !== "canceled");
            // WS-H1 (LB-5): a finished batch that lost items to a transient failure offers to
            // re-run JUST those items. Only once the batch is terminal — retrying while work
            // is still in flight would race the run that is producing the failure rows.
            const roots = retryRoots(batch.jobs);
            const failingKeys = failingItemKeys(batch.jobs, roots);
            const failedItems = failingKeys.size;
            const terminal = !canPause && !canResume;
            const failed = batch.jobs.map(failureReason).find(Boolean);
            // WS-P: a named state for "the bake is not on this device", in the warn tier.
            const noBake = bakeMissing(batch.jobs, status);
            // The item currently being rendered by whichever job in this batch is active.
            const current = batch.jobs.map((j) => live[j.jobId]?.current).find(Boolean);
            return (
              <li
                key={batch.batchId}
                // The ACTIVE batch is marked so it can be picked out at a glance. With the list
                // now newest-first it is usually at the top, but a queued run started while an
                // older one is still going would otherwise look identical to everything else.
                className={`queue__item queue__item--row${status === "running" ? " queue__item--active" : ""}`}
                data-testid="queue-item"
                data-status={status}
                data-active={status === "running" ? "true" : undefined}
                aria-current={status === "running" ? "true" : undefined}
              >
                <div className="queue__main">
                  <button
                  type="button"
                  className="queue__open"
                  onClick={() => setOpenBatchId(batch.batchId)}
                  data-testid="queue-open-batch"
                  title="View batch details"
                >
                  {/* Rule 6: the one row thumbnail, 24x30, declared in theme.css. This was a
                      local 30x48 — the third size the product had invented for the same job. */}
                  <span className="table-thumb" aria-hidden="true" />
                  <span className="queue__main">
                    <b className="queue__name">
                      {transparent
                        ? `${designCount} file${designCount === 1 ? "" : "s"} · Transparent overlays`
                        : `${templateCount} template${templateCount === 1 ? "" : "s"} × ${designCount} design${designCount === 1 ? "" : "s"}`}
                    </b>
                    <small className="queue__sub">
                      {done} / {total} {transparent ? "file" : "frame"}{total === 1 ? "" : "s"} · <RunTime jobs={batch.jobs} status={status} />
                    </small>
                    {current && (
                      <small className="queue__current faint" data-testid="queue-current-item">
                        Processing: <span className="queue__current-name">{current.designSlug} · {current.view}</span>
                      </small>
                    )}
                  </span>
                </button>
                {failed && status === "failed" && (
                  <small className="queue__error" data-testid="queue-error" role="alert"><ErrorNotice detail={failed} /></small>
                )}
                </div>
                <div className="queue__progress-cell">
                  {preparing ? (
                    // WS-N review-1 M-7: "Preparing assets…" is the MOCKUP arm's copy for the
                    // `resolveMockup` await — IndexedDB + AES-GCM decrypt of a template's baked
                    // assets. A folder conversion resolves no assets; what it is doing in this
                    // window is reading the folder and decoding the first file.
                    <small className="queue__preparing faint" data-testid="queue-preparing">
                      {transparent ? "Decoding…" : "Preparing assets…"}
                    </small>
                  ) : (
                    <div className="progress queue__bar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
                      <div className="progress__bar" style={{ width: `${pct}%` }} />
                    </div>
                  )}
                </div>
                <span
                  className={`badge ${noBake ? "badge--warn" : STATUS_BADGE[status]}`}
                  data-testid="queue-status"
                  data-failure-code={noBake ? "bake-missing" : undefined}
                >
                  {noBake ? BAKE_MISSING_LABEL : STATUS_LABEL[status]}
                </span>
                <span className="queue__actions">
                  {canPause && (
                    <RowAction label="Pause" icon="pause" data-testid="queue-pause"
                      onClick={() => batch.jobs.forEach((j) => queue.pause(j.jobId))} />
                  )}
                  {canResume && (
                    <RowAction label="Resume" icon="play" data-testid="queue-resume"
                      onClick={() => batch.jobs.forEach((j) => queue.resume(j.jobId))} />
                  )}
                  {terminal && failedItems > 0 && (
                    <RowAction
                      // The count is part of the NAME, not decoration: "Retry 3 failed" says
                      // how much work the press is about to redo, and a tooltip has room for
                      // it where a 30px button does not.
                      label={`Retry ${failedItems} failed`} icon="refresh" data-testid="queue-retry-failed"
                      onClick={() => {
                        // ONE retry job per still-failing item. Within a retry chain the source
                        // job and its retry both carry a row for the same (design, view), so
                        // dispatching to every job with a failed row enqueued the item twice
                        // (review-1 M-1) — but two TEMPLATES failing the same design × view are
                        // two separate losses and each needs its own dispatch (review-2 M-2).
                        // The key carries the chain root, so both hold. Newest job first — it
                        // holds the latest attempt of its chain.
                        const remaining = new Set(failingKeys);
                        for (const j of [...batch.jobs].sort((a, b) => b.position - a.position)) {
                          const root = roots.get(j.jobId) ?? j.jobId;
                          const owned = j.items.filter((i) => i.status === "failed" && remaining.has(itemKey(root, i)));
                          if (owned.length === 0) continue;
                          queue.retryFailed(j.jobId);
                          for (const i of owned) remaining.delete(itemKey(root, i));
                        }
                      }}
                    />
                  )}
                  {canCancel && (
                    <RowAction label="Cancel" icon="close" tone="danger" data-testid="queue-cancel"
                      onClick={() => batch.jobs.forEach((j) => queue.cancel(j.jobId))} />
                  )}
                  {/* The owner could not find "Open folder" on this panel, and was right not to:
                      it existed only INSIDE the batch-details modal, behind a click on the row.
                      The verb belongs on the row that says the run is running, so the folder is
                      reachable from the screen the operator is already watching. Same string as
                      the Batch panel's and the modal's, learned once; the tooltip is the name
                      (rule 13). Desktop only — `openPath` is absent in a browser. */}
                  {io?.openPath && rowFolder && (
                    <RowAction
                      label="Open folder" icon="folder" tone="quiet" data-testid="queue-open-folder"
                      onClick={() => openFolder(batch.batchId, rowFolder)}
                    />
                  )}
                </span>
                {/* Its own line under the row that produced it, spanning the grid so a long
                    native message does not stretch a column — the same `role="alert"` the modal
                    and the Batch panel use for this exact failure. */}
                {openErr?.batchId === batch.batchId && rowFolder && (
                  <small
                    className="queue__error queue__open-error"
                    role="alert"
                    data-testid="queue-open-error"
                  >
                    <ErrorNotice detail={openErr.message} />
                  </small>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {/* Results land here as they render — the same shared Gallery the Batch tab shows, so
          the operator watches finished mockups appear WITHOUT leaving the Queue page. */}
      <div className="queue__gallery" data-testid="queue-gallery">
        <Gallery
          outputs={outputs}
          progress={batchProgress}
          // WS-H2: same re-read as the Batch panel's gallery — a spilled output is a file on
          // disk, not a missing thumbnail.
          resolveBytes={(o) =>
            o.savedRoot && io.readExportedFile
              ? io.readExportedFile(o.savedRoot, o.localRef)
              : Promise.resolve(null)
          }
        />
      </div>

      {openBatch && (
        <BatchDetails batch={openBatch} live={live} onClose={() => setOpenBatchId(null)} />
      )}
    </section>
  );
}

/** Centered popup with the full make-up of one batch: every template job, its views, frame
 *  progress, and per design×view item results. */
function BatchDetails({
  batch,
  live,
  onClose,
}: {
  batch: BatchGroup;
  live: Record<string, LiveJobProgress>;
  onClose: () => void;
}) {
  const status = batchStatus(batch.jobs);
  const { done, total } = batchFrames(batch.jobs, live);
  const designCount = Math.max(0, ...batch.jobs.map((j) => j.designCount));
  const { io } = useServices();
  // WHERE the mockups went. The popup listed frames and errors but never the destination, so
  // "saved 12 mockups" could not be checked against the folder the operator had in mind — and
  // there was no way to get to the files from here.
  // The BATCH's own folder where one is known (review F-4), falling back to the one global slot
  // for a run that predates the per-job map — the modal's long-standing behaviour.
  const byJob = useBatchStore((st) => st.savedPathByJob);
  const lastSavedPath = useBatchStore((st) => st.lastSavedPath);
  const savedPath = batch.jobs.map((j) => byJob[j.jobId]).find(Boolean) ?? lastSavedPath;
  const [openErr, setOpenErr] = useState<string | null>(null);
  return (
    <Modal
      title="Batch details"
      subtitle={
        batch.jobs.every(isTransparentJob)
          ? `${designCount} file${designCount === 1 ? "" : "s"} · Transparent overlays · ${done} / ${total} converted`
          : `${batch.jobs.length} template${batch.jobs.length === 1 ? "" : "s"} × ${designCount} design${designCount === 1 ? "" : "s"} · ${done} / ${total} frames`
      }
      badge={<span className={`badge ${STATUS_BADGE[status]}`}>{STATUS_LABEL[status]}</span>}
      onClose={onClose}
      testid="batch-details"
    >
      {savedPath && (
        <div className="batch-details__dest" data-testid="batch-details-dest">
          <span className="faint">Saved to</span>
          {/* The full path, selectable — on a narrow modal the tail is what identifies the
              folder, so it is not truncated away. */}
          <code className="batch-details__dest-path" title={savedPath}>{savedPath}</code>
          {io?.openPath && (
            <button
              type="button"
              className="btn btn--sm btn--ghost"
              data-testid="batch-details-open-folder"
              onClick={() => {
                setOpenErr(null);
                void io.openPath!(savedPath).catch((e: unknown) =>
                  setOpenErr(e instanceof Error ? e.message : String(e)));
              }}
            >
              Open folder
            </button>
          )}
          {openErr && <span className="error" role="alert" data-testid="batch-details-open-error"><ErrorNotice detail={openErr} /></span>}
        </div>
      )}
      {batch.jobs.map((job) => {
        const f = framesOf(job, live[job.jobId]);
        const reason = failureReason(job);
        // WS-N review-1 M-6: a transparent item's `view` is `PRIMARY_VIEW`, "a structural
        // placeholder the UI never renders" (`manager.ts`'s item-key convention) — and `views`
        // is `[]`. This popup rendered both anyway: "Apple/a.png · back" and a bare "· views: "
        // with nothing after it. Branch the two strings the way the row already does.
        const transparent = isTransparentJob(job);
        return (
          <div key={job.jobId} className="batch-details__job" data-testid="batch-details-job">
            <div className="batch-details__job-head">
              <b className="batch-details__job-name">{job.templateName ?? job.templateId}</b>
              <span className={`badge ${STATUS_BADGE[job.status]}`}>{STATUS_LABEL[job.status]}</span>
            </div>
            <div className="batch-details__job-meta faint">
              {f.done} / {f.total} {transparent ? "files" : "frames"} · <RunTime jobs={[job]} status={job.status} />
              {!transparent && <>{" · views: "}{job.views.join(", ")}</>}
            </div>
            {reason && <div className="batch-details__job-error" role="alert"><ErrorNotice detail={reason} /></div>}
            {job.items.length > 0 && (
              <ul className="batch-details__items">
                {job.items.map((it, i) => (
                  <li key={`${it.designSlug}-${it.view}-${i}`} className="batch-details__item" data-status={it.status}>
                    <span className="batch-details__item-name">
                      {transparent ? it.designSlug : `${it.designSlug} · ${it.view}`}
                    </span>
                    <span className={`badge ${it.status === "done" ? "badge--success" : it.status === "failed" ? "badge--danger" : "badge--neutral"}`}>
                      {it.status}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </Modal>
  );
}
